import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Skeleton Component
const ClientCardSkeleton: React.FC = () => (
  <div className="client-card">
    <div className="client-card__header">
      <div className="skeleton skeleton--circle" style={{ width: 48, height: 48 }} />
      <div className="client-card__info">
        <div className="skeleton" style={{ height: 16, width: '70%', marginBottom: 6 }} />
        <div className="skeleton" style={{ height: 12, width: '50%' }} />
      </div>
    </div>
    <div className="client-card__details">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="client-card__row">
          <div className="skeleton" style={{ height: 12, width: '30%' }} />
          <div className="skeleton" style={{ height: 12, width: '40%' }} />
        </div>
      ))}
    </div>
  </div>
);

const Clients: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [clients, setClients] = useState<any[]>([]);

  useEffect(() => {
    const token = localStorage.getItem('igtoken');
    axios({
      method: 'get',
      url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/clientlist`,
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res: any) => {
        setClients(res.data || []);
      })
      .catch((error: any) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/login");
        } else {
          setClients([]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [navigate]);

  const filteredanys = clients.filter((client) => {
    return (
      client.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.contactName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleCreateQuote = (client: any) => {
    // Store selected client and navigate to new quote
    sessionStorage.setItem('selectedany', JSON.stringify(client));
    navigate('/new-quote');
  };

  return (
    <div className="clients-page">
      {/* Search and View Controls */}
      <div className="page-controls">
        <div className="search-input-wrapper">
          <svg
            className="search-input-wrapper__icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="search-input-wrapper__input"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="view-toggle">
          <button 
            className={`view-toggle__btn ${viewMode === 'grid' ? 'view-toggle__btn--active' : ''}`}
            onClick={() => setViewMode('grid')}
            aria-label="Grid view"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
          </button>
          <button 
            className={`view-toggle__btn ${viewMode === 'list' ? 'view-toggle__btn--active' : ''}`}
            onClick={() => setViewMode('list')}
            aria-label="List view"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Results Count */}
      <p className="results-count">
        {isLoading ? 'Loading...' : `${filteredanys.length} clients found`}
      </p>

      <div className={`clients-grid ${viewMode === 'list' ? 'clients-grid--list' : ''}`}>
        {isLoading ? (
          <>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <ClientCardSkeleton key={i} />
            ))}
          </>
        ) : (
          filteredanys.map((client: any) => (
            <div key={client.id} className="client-card">
              <div className="client-card__header">
                <div className="client-card__avatar">
                  {getInitials(client.companyName)}
                </div>
                <div className="client-card__info">
                  <h3 className="client-card__name">{client.companyName}</h3>
                  <p className="client-card__email">{client.contactName}</p>
                </div>
              </div>
              <div className="client-card__details">
                <div className="client-card__row">
                  <span className="client-card__label">Email</span>
                  <span className="client-card__value">{client.email}</span>
                </div>
                <div className="client-card__row">
                  <span className="client-card__label">Phone</span>
                  <span className="client-card__value">{client.phone}</span>
                </div>
                <div className="client-card__row">
                  <span className="client-card__label">Location</span>
                  <span className="client-card__value">
                    {client.city}, {client.state}
                  </span>
                </div>
                <div className="client-card__row">
                  <span className="client-card__label">Quotes</span>
                  <span className="client-card__quotes-badge">
                    {client.quotesCount} quotes
                  </span>
                </div>
              </div>
              <div className="client-card__actions">
                <button 
                  className="client-card__btn client-card__btn--primary"
                  onClick={() => handleCreateQuote(client)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  New Quote
                </button>
                <button className="client-card__btn client-card__btn--outline">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  View
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Empty State */}
      {!isLoading && filteredanys.length === 0 && (
        <div className="empty-state">
          <div className="empty-state__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h3 className="empty-state__title">No clients found</h3>
          <p className="empty-state__text">Try adjusting your search query</p>
        </div>
      )}
    </div>
  );
};

export default Clients;
